/**
 * SlideRenderer - Handles visual state management and CSS classes
 * 
 * Responsibilities:
 * - Apply CSS class modifiers for different states
 * - Handle cursor management
 * - Manage opacity/visibility transitions
 * - Coordinate visual updates
 */
export class SlideRenderer {
    constructor(slideElement, config, carouselConfig = null) {
        this.slideElement = slideElement;
        this.config = config;
        this.carouselConfig = carouselConfig || config;
        
        // State
        this.slideType = null;
        this.proximityDistance = undefined;
        this.isActive = false;
        this.isLoading = false;
        this.isLoaded = false;
        this.hasError = false;
        this.hasTimeout = false;
        this.hasPlaceholder = false;
        this.hasFullImage = false;
        this.isZoomable = false;
        this.isZoomed = false;
        this.isInitialized = false;
        this.imageFit = 'contain';
        this.canPan = false;
        
        // Event listeners
        this.listeners = new Map();
    }
    
    /**
     * Add event listener
     */
    addEventListener(event, callback) {
        if (!this.listeners.has(event)) {
            this.listeners.set(event, []);
        }
        this.listeners.get(event).push(callback);
    }
    
    /**
     * Emit event to listeners
     */
    emit(event, data = {}) {
        const callbacks = this.listeners.get(event);
        if (callbacks) {
            callbacks.forEach(callback => callback(data));
        }
    }
    
    /**
     * Set active state
     */
    setActive(active) {
        this.isActive = active;
        this.render();
    }

    /**
     * Set active state without triggering render
     */
    setActiveWithoutRender(active) {
        this.isActive = active;
    }

    /**
     * Set proximity state based on signed distance from active slide
     */
    setProximityState(signedDistance) {
        // Store proximity state for render method
        this.proximityDistance = signedDistance;
        
        // Apply proximity classes immediately
        this.applyProximityClasses();
    }
    
    /**
     * Apply proximity classes based on stored proximity distance
     */
    applyProximityClasses() {
        // Early return if carousel config is not available or proximity distance not set
        if (!this.carouselConfig || typeof this.proximityDistance === 'undefined') return;
        
        // Clear all proximity classes
        this.clearProximityClasses();
        
        const signedDistance = this.proximityDistance;
        const maxDistance = this.carouselConfig.maxProximityDistance || 2;
        const absDistance = Math.abs(signedDistance);
        
        // Apply general direction classes
        if (signedDistance < 0) {
            // Backward/Previous slides
            this.slideElement.classList.add(
                `${this.config.baseHtmlClass}--${this.carouselConfig.backwardClassModifier}`
            );
        } else if (signedDistance > 0) {
            // Forward/Next slides
            this.slideElement.classList.add(
                `${this.config.baseHtmlClass}--${this.carouselConfig.forwardClassModifier}`
            );
        }
        
        // Apply specific distance classes
        if (signedDistance === 0) {
            // Current slide
            this.slideElement.classList.add(
                `${this.config.baseHtmlClass}--${this.carouselConfig.currentClassModifier}`
            );
        } else if (absDistance <= maxDistance) {
            // Within tracked range
            const direction = signedDistance < 0 
                ? this.carouselConfig.prevClassPrefix 
                : this.carouselConfig.nextClassPrefix;
            this.slideElement.classList.add(
                `${this.config.baseHtmlClass}--${direction}-${absDistance}`
            );
        } else {
            // Beyond tracked range
            const farModifier = signedDistance < 0 
                ? this.carouselConfig.farPrevClassModifier 
                : this.carouselConfig.farNextClassModifier;
            this.slideElement.classList.add(
                `${this.config.baseHtmlClass}--${farModifier}`
            );
        }
    }

    /**
     * Clear all proximity-related classes
     */
    clearProximityClasses() {
        const maxDistance = this.carouselConfig.maxProximityDistance || 2;
        
        // Clear direction classes
        this.slideElement.classList.remove(
            `${this.config.baseHtmlClass}--${this.carouselConfig.forwardClassModifier}`,
            `${this.config.baseHtmlClass}--${this.carouselConfig.backwardClassModifier}`,
            `${this.config.baseHtmlClass}--${this.carouselConfig.currentClassModifier}`
        );
        
        // Clear specific distance classes
        for (let i = 1; i <= maxDistance; i++) {
            this.slideElement.classList.remove(
                `${this.config.baseHtmlClass}--${this.carouselConfig.prevClassPrefix}-${i}`,
                `${this.config.baseHtmlClass}--${this.carouselConfig.nextClassPrefix}-${i}`
            );
        }
        
        // Clear far classes
        this.slideElement.classList.remove(
            `${this.config.baseHtmlClass}--${this.carouselConfig.farPrevClassModifier}`,
            `${this.config.baseHtmlClass}--${this.carouselConfig.farNextClassModifier}`
        );
    }
    
    /**
     * Set loading state
     */
    setLoadingState(loading) {
        this.isLoading = loading;
        this.render();
    }
    
    /**
     * Set loaded state
     */
    setLoadedState(loaded) {
        this.isLoaded = loaded;
        this.render();
    }
    
    /**
     * Set error state
     */
    setErrorState(error) {
        this.hasError = error;
        this.render();
    }
    
    /**
     * Set timeout state
     */
    setTimeoutState(timeout) {
        this.hasTimeout = timeout;
        this.render();
    }
    
    /**
     * Set placeholder loaded state
     */
    setPlaceholderLoadedState(placeholderLoaded) {
        this.hasPlaceholder = placeholderLoaded;
        this.render();
    }
    
    /**
     * Set full image loaded state
     */
    setFullImageLoadedState(fullImageLoaded) {
        this.hasFullImage = fullImageLoaded;
        this.render();
    }
    
    /**
     * Set zoomable state
     */
    setZoomable(zoomable) {
        this.isZoomable = zoomable;
        this.render();
    }
    
    /**
     * Set zoomed state
     */
    setZoomed(zoomed) {
        this.isZoomed = zoomed;
        this.render();
    }
    
    /**
     * Set initialized state
     */
    setInitialized(initialized) {
        this.isInitialized = initialized;
        this.render();
    }
    
    /**
     * Set image fit mode
     */
    setImageFit(imageFit) {
        this.imageFit = imageFit;
        this.render();
    }
    
    /**
     * Set pan capability
     */
    setCanPan(canPan) {
        this.canPan = canPan;
        this.render();
    }
    
    /**
     * Update multiple states at once
     */
    updateStates(states) {
        Object.keys(states).forEach(key => {
            if (this.hasOwnProperty(key)) {
                this[key] = states[key];
            }
        });
        this.render();
    }
    
    /**
     * Apply or remove a CSS class modifier
     */
    conditionallySetHtmlClassState(condition, classModifier) {
        const className = `${this.config.baseHtmlClass}--${classModifier}`;
        
        if (condition) {
            this.slideElement.classList.add(className);
        } else {
            this.slideElement.classList.remove(className);
        }
    }
    
    /**
     * Update cursor based on current state
     */
    updateCursor() {
        // Only update cursor for image slides
        if (this.slideType !== 'image' || !this.isZoomable || !this.isLoaded) {
            this.slideElement.style.cursor = '';
            return;
        }
        
        if (this.canPan) {
            this.slideElement.style.cursor = 'grab';
        } else {
            this.slideElement.style.cursor = 'zoom-in';
        }
    }
    
    /**
     * Set cursor to grabbing during pan
     */
    setCursorGrabbing() {
        if (this.canPan) {
            this.slideElement.style.cursor = 'grabbing';
        }
    }
    
    /**
     * Reset cursor after pan
     */
    resetCursor() {
        this.updateCursor();
    }
    
    /**
     * Main render method - applies all visual states
     */
    render() {
        // Apply slide type class
        if (this.slideType) {
            this.conditionallySetHtmlClassState(this.slideType === 'image', this.config.imageSlideTypeModifier);
            this.conditionallySetHtmlClassState(this.slideType === 'content', this.config.contentSlideTypeModifier);
        }
        
        // Apply state classes
        this.conditionallySetHtmlClassState(this.isActive, this.config.activeClassModifier);
        this.conditionallySetHtmlClassState(this.isLoading, this.config.loadingClassModifier);
        this.conditionallySetHtmlClassState(this.isLoaded, this.config.loadedClassModifier);
        this.conditionallySetHtmlClassState(this.hasError, this.config.errorClassModifier);
        this.conditionallySetHtmlClassState(this.hasTimeout, this.config.timeoutClassModifier);
        this.conditionallySetHtmlClassState(this.hasPlaceholder, this.config.placeholderLoadedClassModifier);
        this.conditionallySetHtmlClassState(this.hasFullImage, this.config.fullLoadedClassModifier);
        
        // Apply image fit classes only for image slides
        if (this.slideType === 'image' && this.imageFit) {
            this.conditionallySetHtmlClassState(this.imageFit === 'cover', this.config.coverClassModifier);
            this.conditionallySetHtmlClassState(this.imageFit === 'contain', this.config.containClassModifier);
        }
        
        // Apply zoom classes only for image slides
        if (this.slideType === 'image') {
            this.conditionallySetHtmlClassState(this.isZoomable, this.config.zoomableClassModifier);
            this.conditionallySetHtmlClassState(this.isZoomed, this.config.zoomedClassModifier);
        }
        
        // Apply initialized class
        this.conditionallySetHtmlClassState(this.isInitialized, this.config.initializedClassModifier);
        
        // Update cursor
        this.updateCursor();
        
        // Reapply proximity classes after other state classes
        this.applyProximityClasses();
        
        this.emit('rendered', this.getState());
    }
    
    /**
     * Get current render state
     */
    getState() {
        return {
            slideType: this.slideType,
            isActive: this.isActive,
            isLoading: this.isLoading,
            isLoaded: this.isLoaded,
            hasError: this.hasError,
            hasTimeout: this.hasTimeout,
            hasPlaceholder: this.hasPlaceholder,
            hasFullImage: this.hasFullImage,
            isZoomable: this.isZoomable,
            isZoomed: this.isZoomed,
            isInitialized: this.isInitialized,
            imageFit: this.imageFit,
            canPan: this.canPan
        };
    }
    
    /**
     * Clean up resources
     */
    destroy() {
        this.listeners.clear();
        // Reset cursor
        this.slideElement.style.cursor = '';
    }
}